<template>
<fieldset>
    <div v-if="index === 0">
        <div class="row-label"></div>
        <div>
            <label class="required">
                Pipe Size
                ({{ useMetric(1) ? 'DN' : 'NPS' }})
            </label>
        </div>
        <div>
            <label class="required">
                Pipe Type
            </label>
        </div>
        <div class="text" v-show="!mainBranch">
            <label class="required">
                Pipe Length ({{ useMetric(1) ? 'm.' : 'ft.' }})
            </label>
        </div>
        <div class="text" v-show="mainBranch && ['lateral', 'lateral-longitudinal'].includes(orientation)">
            <label class="required">
                Lateral Pipe Length ({{ useMetric(1) ? 'm.' : 'ft.' }})
            </label>
        </div>

        <div class="text" v-show="mainBranch && ['longitudinal', 'lateral-longitudinal'].includes(orientation)">
            <label class="required">
                Longitudinal Pipe Length ({{ useMetric(1) ? 'm.' : 'ft.' }})
            </label>
        </div>

        <div class="text" v-show="mainBranch && ['riser-wall', 'riser-corner'].includes(orientation)">
            <label class="required">
                Vertical Pipe Length ({{ useMetric(1) ? 'm.' : 'ft.' }})
            </label>
        </div>

        <div class="text">
            <label class="required">
                {{ useMetric(1) ? 'Weight per m' : 'Weight per ft' }}
            </label>
        </div>
        <div class="text">
            <label class="required">
                Pipe Weight ({{ useMetric(1) ? 'Kg.' : 'Lbs.' }})
            </label>
        </div>
        <div class="row-edit"></div>
    </div>
    <div>
        <div class="row-label">
            <label v-text="name"></label>
        </div>
        <div>
            <div class="select">
                <select id="pipe-size" v-model="localData.pipeSize">
                    <option value="" v-if="!pipesLoaded">Loading</option>
                    <option v-for="size in sizes" :value="size" v-text="npsOrDn(toFrac(size))"></option>
                </select>
            </div>
        </div>
        <div>
            <div class="select">
                <select id="pipe-type" v-model="localData.pipeType">
                    <option value="" v-if="!pipesLoaded">Loading</option>
                    <option v-for="type in types" :value="type" v-text="type"></option>
                </select>
            </div>
        </div>
        <div class="text" v-show="!mainBranch">
            <input-number :mToFt="useMetric(1)" id="pipe-length" v-model="localData.pipeLength" :min="0.001" :defaultMin="1" required />
        </div>
        <div class="text" v-show="mainBranch && ['lateral', 'lateral-longitudinal'].includes(orientation)">
            <input-number :mToFt="useMetric(1)" id="lateral-pipe-length" v-model="localData.lateralPipeLength" :min="lateralMin" :max="40" required />
        </div>
        <div class="text" v-show="mainBranch && ['longitudinal', 'lateral-longitudinal'].includes(orientation)">
            <input-number :mToFt="useMetric(1)" id="longitudinal-pipe-length" v-model="localData.longitudinalPipeLength" :min="longitudinalMin" :max="80" required />
        </div>
        <div class="text" v-show="mainBranch && ['riser-wall', 'riser-corner'].includes(orientation)">
            <input-number :mToFt="useMetric(1)" id="vertical-pipe-length" v-model="localData.verticalPipeLength" :min="0.001" :max="25" required />
        </div>
        <div class="text">
            <span v-if="record.weightPerFoot" v-text="lbPerFootOrKgPerM(record.weightPerFoot, true)"></span>
        </div>
        <div class="text">
            <span v-if="record.weight" v-text="lbOrKg(record.weight, true)"></span>
        </div>
        <div class="row-edit">
            <i v-if="can.delete" class="fa fa-trash" aria-hidden="true" @click="removeSegment"></i>
        </div>
    </div>
</fieldset>
</template>

<script>
import { mapKeyedGetters } from '../../../common/vuex-extra';
import { mapGetters, mapState } from 'vuex';
import Fraction from 'fraction.js';
import InputNumber from 'vue/common/_inputNumber';

const ALPHA = [
    'a','b','c','d','e','f','g',
    'h','i','j','k','l','m','n',
    'o','p','q','r','s','t','u',
    'v','w','x','y','z'
];


export default {
    name: 'seismic-line-segment',

    components: {
        InputNumber,
    },

    props: [
        'uid',
        'mainBranch',
    ],

    data() {
        return {
            localData: {}
        };
    },

    mounted() {
        this.localData = Object.assign({}, this.record);
    },

    watch: {
        localData: {
            handler() {
                this.$store.dispatch('seismicSystem/setSegment', this.localData);
            },
            deep: true
        },
    },

    computed: {
        ...mapState({
            "pipesLoaded": state => state.seismicPipes.loaded,
            "orientation": state => state.seismicSystem.orientation
        }),

        ...mapKeyedGetters({
            "record": "seismicSystem/segment",
            "index": "seismicSystem/segmentIndex",
            "siblings": "seismicSystem/segmentSiblings"
        }),

        ...mapGetters({
            "largestMainBranchPipeSize": "seismicSystem/largestMainBranchPipeSize",
            "mainPipeSize": "seismicSystem/mainPipeSize",

        }),

        longitudinalMin() {
            if ('lateral-longitudinal' == this.orientation) {
                if (this.localData.lateralPipeLength === null) {
                    return 0;
                }
                return Number(this.localData.lateralPipeLength) === 0 ? 0.001 : 0;
            }

            return 0.001;
        },

        lateralMin() {
            if ('lateral-longitudinal' == this.orientation) {
                if (this.localData.longitudinalPipeLength === null) {
                    return 0;
                }
                return Number(this.localData.longitudinalPipeLength) === 0 ? 0.001 : 0;
            }

            return 0.001;
        },

        types() {
            if (!this.localData.pipeSize) {
                return this.$store.getters['seismicPipes/types'];
            } else {
                return this.$store.getters['seismicPipes/typesFromSize'](this.localData.pipeSize);
            }
        },

        sizes() {
            let sizes = [];
            if (!this.localData.pipeType) {
                sizes = this.$store.getters['seismicPipes/sizes'];
            } else {
                sizes = this.$store.getters['seismicPipes/sizesFromType'](this.localData.pipeType);
            }

            // Don't show lower than size 1 in the Main,
            // Main segments and in Riser Nipple sizes
            if (this.mainBranch || this.record.riserNipple) {
                sizes = sizes.filter(s => s >= 1);
            }

            // Branch segments can have a max size of 3
            if (!this.mainBranch && !this.record.riserNipple) {
                sizes = sizes.filter(s => s <= 3);
            }

            // Segments can't exceen the Main
            if (!this.mainBranch || this.mainBranch && this.index !== 0 && !this.record.riserNipple) {
                sizes = sizes.filter(s => s <= this.mainPipeSize);
            }

            return sizes;
       },

        validPipeSize() {
            if (!this.localData.pipeSize || !this.maxPipeSize) {
                return true;
            }

            if (this.localData.pipeSize > this.maxPipeSize) {
                return false;
            }

            return true;
        },

        maxPipeSize() {
            // Riser nipples can have a max size of the largest size in the Main
            if (this.record.riserNipple === true) {
                return this.mainPipeSize;
            }

            if (this.index === 0) {
                return null;
            }


            return this.mainPipeSize;
        },

        name() {
            if (this.localData.riserNipple) {
                return 'Riser Nipple';
            }

            if (this.mainBranch && this.index === 0) {
                return 'Main';
            }

            return ALPHA[this.index] ? ALPHA[this.index].toUpperCase() : null;
        },

        can() {
            return {
                delete: !(this.mainBranch && (this.record.riserNipple || this.index === 0))
            };
        },
    },

    methods: {
        removeSegment() {
            this.$store.dispatch('seismicSystem/removeSegment', this.localData);
        },
        toFrac: n => (new Fraction(n)).toFraction(true),
    }
}
</script>
